/**
 * 判断边选择是否携带可用于 Worker 回绑的权威拓扑提示。
 *
 * 设计说明：
 * 1. 相邻面引用是最强语义，但布尔切除/特征结果体上的部分边可能暂时缺少 adjacentFaceRefs；
 * 2. 权威拓扑快照提供的 `subEntityId + persistentRef + sourceObjectId` 已足够驱动 BlendEdgeTopologyMatcher；
 * 3. web-client 与 chamfer 草稿校验必须共用该规则，避免“能预览却不能提交”或“永远不能预览”的双轨门禁。
 *
 * @param runtimeHint 运行时拓扑提示。
 * @returns 是否具备可用于 blend 求解的权威边提示。
 */
export function hasAuthoritativeBlendEdgeRuntimeHint(
  runtimeHint: RuntimeTopologyHint | undefined,
): boolean {
  if (runtimeHint?.kind !== 'edge') {
    return false;
  }

  const hasCoreIdentity = Number.isInteger(runtimeHint.topologyIndex)
    && typeof runtimeHint.sourceObjectId === 'string'
    && runtimeHint.sourceObjectId.length > 0
    && typeof runtimeHint.persistentRef === 'string'
    && runtimeHint.persistentRef.length > 0;
  if (!hasCoreIdentity) {
    return false;
  }

  if (typeof runtimeHint.subEntityId === 'string' && runtimeHint.subEntityId.length > 0) {
    return true;
  }

  return (
    (Array.isArray(runtimeHint.adjacentFaceHints)
      && runtimeHint.adjacentFaceHints.length > 0
      && runtimeHint.adjacentFaceHints.every((faceHint) => (
        Number.isInteger(faceHint.topologyIndex)
        && typeof faceHint.persistentRef === 'string'
        && faceHint.persistentRef.length > 0
      )))
    || (Array.isArray(runtimeHint.adjacentFaceRefs) && runtimeHint.adjacentFaceRefs.length > 0)
  );
}

/**
 * 判断 blend 选择摘要是否具备权威拓扑提示。
 *
 * @param item 选择摘要。
 * @returns 是否具备权威提示。
 */
export function hasAuthoritativeBlendSelectionRuntimeHint(item: {
  kind: BlendSelectionKind;
  persistentRef?: string;
  bodyObjectId?: string;
  subEntityId?: string;
  runtimeTopologyHint?: RuntimeTopologyHint;
}): boolean {
  if (item.kind !== 'edge') {
    return true;
  }

  if (
    typeof item.subEntityId === 'string'
    && item.subEntityId.length > 0
    && typeof item.persistentRef === 'string'
    && item.persistentRef.length > 0
    && typeof item.bodyObjectId === 'string'
    && item.bodyObjectId.length > 0
  ) {
    return true;
  }

  return hasAuthoritativeBlendEdgeRuntimeHint(item.runtimeTopologyHint);
}
